import { CourseDTO } from '../../services/apiClient';
import { TableAction } from '../common/Table';
import { Check, Clock, DollarSign, Download, FileText, Mail, MessageSquareText, Package, RotateCcw, Search, Star, Trash2, User, UserCog } from 'lucide-react';
import { CourseMaterialTypeConstants } from '../../constants/CourseMaterialTypeConstansts';
import { CourseStatusConstants } from '../../constants/CourseStatusConstants';

interface CourseActionHandlers {
  onViewMemberDetails: (course: CourseDTO) => void;
  onView: (course: CourseDTO) => void;
  onViewEvaluation: (course: CourseDTO) => void;
  onDelete: (course: CourseDTO) => Promise<void>;
  onSendEmail: (member: CourseDTO) => void;
  onViewAllEmails: (member: CourseDTO) => void;
  onViewRecordEmails: (member: CourseDTO) => void;
  onRestore: (course: CourseDTO) => Promise<void>;
  onApprove: (course: CourseDTO) => void;
  onShipped: (course: CourseDTO) => void;
  onChangeStatus: (course: CourseDTO) => void;
  onImpersonateMemberLogin: (course: CourseDTO) => void;
  onAddNote: (course: CourseDTO) => void;
  onViewNoteHistory: (course: CourseDTO) => void;
  onViewInvoiceHistory: (course: CourseDTO) => void;
  onSendProofOfPaymentLink: (course: CourseDTO) => void;
  onCompleteEftPayment: (course: CourseDTO) => void;
  onDownloadForfeitureDocument?: (course: CourseDTO) => void;
  onDownloadOriginalCertificate?: (course: CourseDTO) => void;
}

const createCourseActions =
  (handlers: CourseActionHandlers) =>
    (row: CourseDTO): TableAction[] => {
      const actions: TableAction[] = [];
      const isForfeited = row.courseStatusId?.toLowerCase() === CourseStatusConstants.FORFEITED;

      if (!row.isDeleted && !isForfeited) {
        actions.push({
          label: 'View Member',
          onClick: () => handlers.onViewMemberDetails(row),
          icon: <User className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onView) {
        actions.push({
          label: 'View Record',
          onClick: () => handlers.onView(row),
          icon: <Search className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onViewEvaluation) {
        actions.push({
          label: 'Evaluations',
          onClick: () => handlers.onViewEvaluation(row),
          icon: <Search className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && row.member?.userID && handlers.onImpersonateMemberLogin) {
        actions.push({
          label: 'Login as Member',
          onClick: () => handlers.onImpersonateMemberLogin(row),
          icon: <UserCog className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to login as this member?',
        });
      }


      if (!row.isDeleted && !isForfeited && handlers.onSendEmail) {
        actions.push({
          label: 'Send Message',
          onClick: () => handlers.onSendEmail(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onViewAllEmails) {
        actions.push({
          label: 'Messages(All)',
          onClick: () => handlers.onViewAllEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onViewRecordEmails) {
        actions.push({
          label: 'Messages(Record)',
          onClick: () => handlers.onViewRecordEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onSendProofOfPaymentLink) {
        actions.push({
          label: 'Email Proof Upload?',
          onClick: () => handlers.onSendProofOfPaymentLink(row),
          icon: <Mail className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to send the proof of payment link to this member?',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onAddNote) {
        actions.push({
          label: 'Create Note',
          onClick: () => handlers.onAddNote(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onViewNoteHistory) {
        actions.push({
          label: 'Notes History',
          onClick: () => handlers.onViewNoteHistory(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }


      if (!row.isDeleted && !isForfeited && handlers.onCompleteEftPayment) {
        actions.push({
          label: 'Paid',
          onClick: () => handlers.onCompleteEftPayment(row),
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600 hover:text-green-700',
          confirmMessage: 'Are you sure you want to mark this course as paid?',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onApprove) {
        actions.push({
          label: 'Approve',
          onClick: () => handlers.onApprove(row),
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600 hover:text-green-700',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onShipped && row.courseMaterialTypeId === CourseMaterialTypeConstants.PRINT) {
        actions.push({
          label: 'Shipped',
          onClick: () => handlers.onShipped(row),
          icon: <Package className="w-4 h-4" />,
          className: 'text-blue-600 hover:text-blue-700',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onChangeStatus) {
        actions.push({
          label: 'Change Status',
          onClick: () => handlers.onChangeStatus(row),
          icon: <Star className="w-4 h-4" />,
          className: 'text-orange-600 hover:text-orange-700',
        });
      }



      if (!row.isDeleted && !isForfeited && handlers.onViewInvoiceHistory) {
        actions.push({
          label: 'Invoice History',
          onClick: () => handlers.onViewInvoiceHistory(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && isForfeited && handlers.onDownloadOriginalCertificate) {
        actions.push({
          label: 'Download Original Certificate',
          onClick: () => handlers.onDownloadOriginalCertificate!(row),
          icon: <Download className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && isForfeited && handlers.onDownloadForfeitureDocument) {
        actions.push({
          label: 'Download Forfeiture Document',
          onClick: () => handlers.onDownloadForfeitureDocument!(row),
          icon: <FileText className="w-4 h-4" />,
        });
      }

      if (row.isDeleted) {
        actions.push({
          label: 'Restore Deleted',
          onClick: () => handlers.onRestore(row),
          icon: <RotateCcw className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to restore this course?',
          className: 'text-green-600 hover:text-green-700',
        });
      } else if (!isForfeited) {
        actions.push({
          label: 'Delete Record',
          onClick: () => handlers.onDelete(row),
          icon: <Trash2 className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to delete this course?',
          className: 'text-red-600 hover:text-red-700',
        });
      }

      return actions;
    };

export default createCourseActions;
